import { select, put, all, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { fetchDecisions } from 'APIPackage/DecisionModule/services/decisionListApi';
import { IDecisionResponse } from 'APIPackage/DecisionModule/interfaces/IDecisionResponse';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { fetchDecisionDelete } from 'APIPackage/DecisionModule/services/decisionApi';

import {
  decisionListGetData,
  decisionListRemoveDecisions,
  decisionListSetData,
  decisionListFindDecision,
  decisionListSetFindData,
} from '../actions/decisionListActions';
import { DecisionListActionsEnum } from '../enums/DecisionListActionsEnum';
import { mapRawToDecision } from '../common/mapRawToDecision';
import {
  getDecisionListData,
  getDecisionListFilterParam,
  getDecisionListOrder,
  getDecisionListPage,
  getDecisionListSearchValue,
} from '../selectors/decisionListSelectors';
import { getRequestFilterData } from '../common/getRequestFilterData';
import { IDecisionListFilterParams } from '../interfaces/IDecisionListFilterParams';

const PAGE_SIZE = 50;

export function* watchDecisionListSaga() {
  yield takeEvery(DecisionListActionsEnum.DECISION_LIST_INIT_PAGE, initPageSaga);
  yield takeEvery(DecisionListActionsEnum.DECISION_LIST_GET_DATA, fetchDecisionListSaga);
  yield takeEvery(DecisionListActionsEnum.DECISION_LIST_GET_DATA_NEXT_PAGE, fetchDecisionListNextPageSaga);
  yield takeEvery(DecisionListActionsEnum.DECISION_LIST_REMOVE_DECISIONS, removeDecisionsSaga);
  yield takeEvery(DecisionListActionsEnum.DECISION_LIST_SET_ORDER, changeOrderSaga);
  yield takeEvery(DecisionListActionsEnum.DECISION_LIST_FIND_DECISION, decisionListFindKnmSaga);
}

function* initPageSaga() {
  yield put(decisionListGetData());
}

function* fetchDecisionListSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order: string | null = getDecisionListOrder(state);
    const searchString = getDecisionListSearchValue(state);
    const filterParam = getDecisionListFilterParam(state);
    const requestData: IDecisionListFilterParams | null = getRequestFilterData(searchString || '', filterParam);
    const res: IDecisionResponse = yield fetchDecisions(0, PAGE_SIZE, requestData, order ?? '');
    const decisions = res.list.map(mapRawToDecision);

    yield put(decisionListSetData(decisions, res.page, res.pageCount, res.totalCount, res.rejected));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionListFindKnmSaga(action: ActionType<typeof decisionListFindDecision>) {
  function* funcTry() {
    const { filterParam, size } = action.payload;

    const res: IDecisionResponse = yield fetchDecisions(0, size, filterParam);

    if (res) {
      yield put(decisionListSetFindData(res.list));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* fetchDecisionListNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const data = getDecisionListData(state);
    const page = getDecisionListPage(state);
    const order = getDecisionListOrder(state);
    const searchString = getDecisionListSearchValue(state);
    const requestData: IDecisionListFilterParams | null = getRequestFilterData(searchString || '');

    const res: IDecisionResponse = yield fetchDecisions(page + 1, PAGE_SIZE, requestData, order ?? '');
    const decisions = res.list.map(mapRawToDecision);

    yield put(decisionListSetData([...data, ...decisions], res.page, res.pageCount, res.totalCount, res.rejected));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* removeDecisionsSaga(action: ActionType<typeof decisionListRemoveDecisions>) {
  function* funcTry() {
    const { ids } = action.payload;

    yield all(ids.map(id => fetchDecisionDelete(id)));
    yield put(decisionListGetData());
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.DELETE_SUCCESS,
        renderContent: ids.length > 1 ? 'Решения успешно удалены.' : 'Решение успешно удалено.',
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* changeOrderSaga() {
  yield put(decisionListGetData());
}
